import React, { PropsWithChildren, useEffect } from 'react';
import classNames from 'clsx';
import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import type { Transform } from '@dnd-kit/utilities';

import { styled } from '@nexelem/deprecated-theme';

type DraggableItemProps = {
  dragOverlay?: boolean;
  disabled?: boolean;
  dragging?: boolean;
  height?: number;
  index?: number;
  fadeIn?: boolean;
  transform?: Transform | null;
  listeners?: DraggableSyntheticListeners;
  transition?: string | null;
};

const Container = styled('div')(() => {
  return {
    display: 'flex',
    boxSizing: 'border-box',
    transformOrigin: '0 0',
    touchAction: 'manipulation',
    transform:
      'translate3d(var(--translate-x, 0), var(--translate-y, 0), 0) scaleX(var(--scale-x, 1)) scaleY(var(--scale-y, 1))',
  };
});

export const DraggableItem = React.memo(
  React.forwardRef<HTMLDivElement, PropsWithChildren<DraggableItemProps>>(
    ({ dragOverlay, dragging, index, listeners, transition, transform, children, ...props }, ref) => {
      useEffect(() => {
        if (!dragOverlay) {
          return;
        }

        document.body.style.cursor = 'grabbing';

        return () => {
          document.body.style.cursor = '';
        };
      }, [dragOverlay]);

      return (
        <Container
          ref={ref}
          style={
            {
              transition,
              '--translate-x': transform ? `${Math.round(transform.x)}px` : undefined,
              '--translate-y': transform ? `${Math.round(transform.y)}px` : undefined,
              '--scale-x': transform?.scaleX ? `${transform.scaleX}` : undefined,
              '--scale-y': transform?.scaleY ? `${transform.scaleY}` : undefined,
              '--index': index,
            } as React.CSSProperties
          }
        >
          <div className={classNames('w-full', dragging && !dragOverlay && 'opacity-50')} {...listeners} {...props}>
            {children}
          </div>
        </Container>
      );
    },
  ),
);
